import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Map, Search, Mail, Users, FileText } from 'lucide-react';
const features = [{
  icon: <Map className="w-5 h-5" data-id="element-6295" />,
  title: 'Plan tours with real constraints',
  description: 'Set your dates, region, fee range, and routing rules. Booker turns them into a realistic campaign.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6296">
        <div className="flex justify-between items-center mb-4" data-id="element-6297">
          <div data-id="element-6298">
            <div className="text-sm font-semibold mb-1" data-id="element-6299">Summer Run '26</div>
            <div className="text-xs text-textMuted" data-id="element-6300">
              Jun 10 - Jun 25 • Midwest to New England
            </div>
          </div>
          <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded" data-id="element-6301">
            14 stops
          </div>
        </div>
        <div className="relative h-40 border border-border rounded bg-background/50" data-id="element-6302">
          <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(#27272A 1px, transparent 1px)',
        backgroundSize: '16px 16px',
        opacity: 0.2
      }} data-id="element-6303"></div>
          <div className="absolute top-1/4 left-1/4 w-3 h-3 rounded-full bg-primary shadow-[0_0_8px_rgba(245,158,11,0.5)]" data-id="element-6304"></div>
          <div className="absolute top-1/3 left-1/2 w-3 h-3 rounded-full bg-primary shadow-[0_0_8px_rgba(245,158,11,0.5)]" data-id="element-6305"></div>
          <div className="absolute top-1/4 right-1/4 w-3 h-3 rounded-full bg-border border-2 border-primary" data-id="element-6306"></div>
          <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-6307">
            <path d="M 25% 25% L 50% 33% L 75% 25%" fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="3 3" className="opacity-40" data-id="element-6308" />
          </svg>
        </div>
      </div>
}, {
  icon: <Search className="w-5 h-5" data-id="element-6309" />,
  title: 'Find venues that actually fit',
  description: 'Search by capacity, geography, genre, route leg, and contact quality.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64 overflow-hidden" data-id="element-6310">
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-background border border-border rounded-md text-xs text-textMuted" data-id="element-6311">
          <Search className="w-3 h-3" data-id="element-6312" />
          Folk venues • 200-500 cap • Vermont
        </div>
        <div className="space-y-2" data-id="element-6313">
          {[{
        name: 'Higher Ground',
        city: 'Burlington, VT',
        cap: '400',
        fit: '94%'
      }, {
        name: 'ArtsRiot',
        city: 'Burlington, VT',
        cap: '250',
        fit: '89%'
      }, {
        name: 'The Skinny Pancake',
        city: 'Burlington, VT',
        cap: '150',
        fit: '82%'
      }].map(venue => <div key={venue.name} className="bg-surface border border-border rounded-lg p-3 flex justify-between items-center" data-id="element-6314">
              <div data-id="element-6315">
                <div className="text-sm font-medium" data-id="element-6316">{venue.name}</div>
                <div className="text-xs text-textMuted" data-id="element-6317">
                  {venue.city} • {venue.cap} cap
                </div>
              </div>
              <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded font-medium" data-id="element-6318">
                {venue.fit} fit
              </div>
            </div>)}
        </div>
      </div>
}, {
  icon: <Mail className="w-5 h-5" data-id="element-6319" />,
  title: 'Let Booker do the repetitive work',
  description: 'Use templates, AI-assisted personalization, and follow-up sequences without losing control.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6320">
        <div className="flex justify-between items-center mb-3" data-id="element-6321">
          <div className="text-xs font-semibold text-textMuted uppercase tracking-wider" data-id="element-6322">
            Outreach Sequence
          </div>
          <div className="px-2 py-1 bg-surface border border-border rounded text-xs" data-id="element-6323">
            25 venues
          </div>
        </div>
        <div className="space-y-3" data-id="element-6324">
          <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6325">
            <div className="flex items-center gap-2 mb-2" data-id="element-6326">
              <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center text-xs font-bold text-primary" data-id="element-6327">
                1
              </div>
              <div className="text-xs font-medium" data-id="element-6328">Initial outreach</div>
              <div className="ml-auto px-2 py-0.5 bg-green-500/10 text-green-500 text-[10px] rounded" data-id="element-6329">
                Sent
              </div>
            </div>
            <div className="text-xs text-textMuted" data-id="element-6330">
              Subject: June tour through Vermont...
            </div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6331">
            <div className="flex items-center gap-2 mb-2" data-id="element-6332">
              <div className="w-6 h-6 rounded bg-background border border-border flex items-center justify-center text-xs font-medium text-textMuted" data-id="element-6333">
                2
              </div>
              <div className="text-xs font-medium text-textMuted" data-id="element-6334">
                Follow-up (3 days)
              </div>
              <div className="ml-auto px-2 py-0.5 bg-border text-textMuted text-[10px] rounded" data-id="element-6335">
                Scheduled
              </div>
            </div>
            <div className="text-xs text-textMuted" data-id="element-6336">
              Subject: Following up on June dates...
            </div>
          </div>
        </div>
      </div>
}, {
  icon: <Users className="w-5 h-5" data-id="element-6337" />,
  title: 'Make sure the band can actually play the gig',
  description: 'Track lineup availability, conflicts, substitute options, and show lock status.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6338">
        <div className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-3" data-id="element-6339">
          Lineup Status
        </div>
        <div className="space-y-2" data-id="element-6340">
          {[{
        name: 'Mark (Guitar/Vocals)',
        status: 'confirmed',
        color: 'green'
      }, {
        name: 'Sarah (Bass)',
        status: 'confirmed',
        color: 'green'
      }, {
        name: 'Tom (Drums)',
        status: 'conflict Jun 14',
        color: 'red'
      }, {
        name: 'Lisa (Keys)',
        status: 'pending',
        color: 'amber'
      }].map(member => <div key={member.name} className="bg-surface border border-border rounded-lg p-3 flex justify-between items-center" data-id="element-6341">
              <div className="text-sm" data-id="element-6342">{member.name}</div>
              <div className={`px-2 py-1 bg-${member.color}-500/10 text-${member.color}-500 text-xs rounded`} data-id="element-6343">
                {member.status}
              </div>
            </div>)}
        </div>
        <div className="mt-4 pt-3 border-t border-border text-xs text-textMuted" data-id="element-6344">
          3 of 4 required members confirmed
        </div>
      </div>
}, {
  icon: <FileText className="w-5 h-5" data-id="element-6345" />,
  title: 'Handle the paperwork and money',
  description: 'Contracts, deposits, itinerary details, and post-show finance all live in the same workflow.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6346">
        <div className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-3" data-id="element-6347">
          Show Finance
        </div>
        <div className="space-y-3" data-id="element-6348">
          <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6349">
            <div className="flex justify-between items-center mb-2" data-id="element-6350">
              <div className="text-sm font-medium" data-id="element-6351">The Empty Bottle</div>
              <div className="text-sm font-bold text-primary" data-id="element-6352">$1,500</div>
            </div>
            <div className="flex gap-2 text-xs" data-id="element-6353">
              <div className="px-2 py-1 bg-green-500/10 text-green-500 rounded" data-id="element-6354">
                Contract signed
              </div>
              <div className="px-2 py-1 bg-green-500/10 text-green-500 rounded" data-id="element-6355">
                Deposit received
              </div>
            </div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6356">
            <div className="flex justify-between items-center mb-2" data-id="element-6357">
              <div className="text-sm font-medium" data-id="element-6358">Beachland Tavern</div>
              <div className="text-sm font-bold text-primary" data-id="element-6359">$1,800</div>
            </div>
            <div className="flex gap-2 text-xs" data-id="element-6360">
              <div className="px-2 py-1 bg-green-500/10 text-green-500 rounded" data-id="element-6361">
                Contract signed
              </div>
              <div className="px-2 py-1 bg-amber-500/10 text-amber-500 rounded" data-id="element-6362">
                Awaiting deposit
              </div>
            </div>
          </div>
        </div>
      </div>
}];
export function ForArtistsPage() {
  return <div data-id="element-6363">
      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden" data-id="element-6364">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" data-id="element-6365"></div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-6366">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-5xl md:text-6xl font-bold tracking-tight mb-6 leading-tight" data-id="element-6367">
            Book more shows — without turning booking into a second full-time
            job.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-lg md:text-xl text-textMuted mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-6368">
            Plan better routes, find matching venues, automate outreach,
            negotiate inside your rules, lock your lineup, and manage every show
            in one place.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-6369">
            <Link to="/onboarding" className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-background bg-primary rounded-lg hover:bg-primaryHover transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)]" data-id="element-6370">
              Start free
              <ChevronRight className="w-4 h-4" data-id="element-6371" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      {features.map((feature, index) => <section key={index} className={`py-20 ${index % 2 === 0 ? 'bg-surface/10 border-y border-border' : ''}`} data-id="element-6372">
          <div className="max-w-6xl mx-auto px-6" data-id="element-6373">
            <div className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-id="element-6374">
              <motion.div initial={{
            opacity: 0,
            x: index % 2 === 0 ? -30 : 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className={index % 2 === 1 ? 'md:order-2' : ''} data-id="element-6375">
                <div className="w-12 h-12 rounded-lg bg-background border border-border flex items-center justify-center text-primary mb-6" data-id="element-6376">
                  {feature.icon}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-6377">
                  {feature.title}
                </h2>
                <p className="text-lg text-textMuted leading-relaxed" data-id="element-6378">
                  {feature.description}
                </p>
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            x: index % 2 === 0 ? 30 : -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className={index % 2 === 1 ? 'md:order-1' : ''} data-id="element-6379">
                {feature.mockup}
              </motion.div>
            </div>
          </div>
        </section>)}

      {/* Final CTA */}
      <section className="py-24 border-t border-border" data-id="element-6380">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-6381">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-6382">
            Ready to book smarter?
          </h2>
          <p className="text-lg text-textMuted mb-10" data-id="element-6383">
            Join artists who are building better tours with less chaos.
          </p>
          <Link to="/onboarding" className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-background bg-primary rounded-lg hover:bg-primaryHover transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)]" data-id="element-6384">
            Start free today
            <ChevronRight className="w-4 h-4" data-id="element-6385" />
          </Link>
        </div>
      </section>
    </div>;
}